import {customElement, property, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {resolve} from "../../../container";
import {Interval} from "../../../common/interval";
import {schedule} from "../../../common/utils/promises";
import {Dictionary} from "../dictionary";
import {UnLitElement} from "../../../common/elements";

type Precision = "seconds" | "minutes" | "hours" | "days";

const ONE_SECOND_IN_MILLIS = 1000;
const ONE_MINUTE_IN_MILLIS = ONE_SECOND_IN_MILLIS * 60;
const ONE_HOUR_IN_MILLIS = ONE_MINUTE_IN_MILLIS * 60;
const ONE_DAY_IN_MILLIS = ONE_HOUR_IN_MILLIS * 24;

abstract class CountdownPrecision {

    public constructor(protected labelSingular: string, protected labelPlural: string) {
    }

    public abstract calculate(millisLeft: number): CountdownPart;

    protected labelledValue(value: number, singular: string, plural: string): CountdownPart {
        return {label: value === 1 ? singular : plural, value: value};
    }
}

type CountdownPart = { label: string, value: number };

class CeilDays extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const days = Math.ceil(millisLeft / ONE_DAY_IN_MILLIS);
        return this.labelledValue(days, this.labelSingular, this.labelPlural);
    }
}

class FloorDays extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const days = Math.floor(millisLeft / ONE_DAY_IN_MILLIS);
        return this.labelledValue(days, this.labelSingular, this.labelPlural);
    }
}


class CeilHours extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const hours = Math.ceil((millisLeft % ONE_DAY_IN_MILLIS) / ONE_HOUR_IN_MILLIS);
        return this.labelledValue(hours, this.labelSingular, this.labelPlural);
    }
}

class FloorHours extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const hours = Math.floor((millisLeft % ONE_DAY_IN_MILLIS) / ONE_HOUR_IN_MILLIS);
        return this.labelledValue(hours, this.labelSingular, this.labelPlural);
    }
}

class CeilMinutes extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const minutes = Math.ceil((millisLeft % ONE_HOUR_IN_MILLIS) / ONE_MINUTE_IN_MILLIS);
        return this.labelledValue(minutes, this.labelSingular, this.labelPlural);
    }
}

class FloorMinutes extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const minutes = Math.floor((millisLeft % ONE_HOUR_IN_MILLIS) / ONE_MINUTE_IN_MILLIS);
        return this.labelledValue(minutes, this.labelSingular, this.labelPlural);
    }
}

class Seconds extends CountdownPrecision {
    public calculate(millisLeft: number): CountdownPart {
        const seconds = Math.floor((millisLeft % ONE_MINUTE_IN_MILLIS) / ONE_SECOND_IN_MILLIS);
        return this.labelledValue(seconds, this.labelSingular, this.labelPlural);
    }
}

class NoValue extends CountdownPrecision {

    public calculate(_: number): CountdownPart {
        return {label: this.labelPlural, value: 0};
    }
}

@customElement("eop-countdown")
export class EopCountdown extends UnLitElement {

    @property({attribute: "target-date", type: Number})
    private targetDate: number;

    @property({attribute: "precision", reflect: true})
    private readonly precision: Precision = "seconds";

    @state()
    private countdownParts: [CountdownPart, CountdownPart, CountdownPart, CountdownPart];

    private days: CountdownPrecision;
    private hours: CountdownPrecision;
    private minutes: CountdownPrecision;
    private seconds: CountdownPrecision;

    private refreshCountdown: Promise<void>;
    private dictionary: Dictionary;

    public constructor(
        private interval: Interval = resolve(Interval)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.createCountdownPrecision(this.precision);

        this.refreshCountdown = schedule(this.interval.repeat(() => this.updateCountdownParts(), ONE_SECOND_IN_MILLIS))
            .as("refreshCountdown");

        this.updateCountdownParts();
    }

    private createCountdownPrecision(precision: Precision): void {
        const labelDaySingular = this.dictionary.translate(`MSG_DAY_LABEL`);
        const labelDayPlural = this.dictionary.translate(`MSG_DAYS_LABEL`);
        const labelHoursSingular = this.dictionary.translate(`MSG_HOUR_LABEL`);
        const labelHoursPlural = this.dictionary.translate(`MSG_HOURS_LABEL`);
        const labelMinutesSingular = this.dictionary.translate(`MSG_MINUTE_LABEL`);
        const labelMinutesPlural = this.dictionary.translate(`MSG_MINUTES_LABEL`);
        const labelSecondsSingular = this.dictionary.translate(`MSG_SECOND_LABEL`);
        const labelSecondsPlural = this.dictionary.translate(`MSG_SECONDS_LABEL`);

        switch (precision) {
            case "days":
                this.days = new CeilDays(labelDaySingular, labelDayPlural);
                this.hours = new NoValue(labelHoursSingular, labelHoursPlural);
                this.minutes = new NoValue(labelMinutesSingular, labelMinutesPlural);
                this.seconds = new NoValue(labelSecondsSingular, labelSecondsPlural);
                break;
            case "hours":
                this.days = new FloorDays(labelDaySingular, labelDayPlural);
                this.hours = new CeilHours(labelHoursSingular, labelHoursPlural);
                this.minutes = new NoValue(labelMinutesSingular, labelMinutesPlural);
                this.seconds = new NoValue(labelSecondsSingular, labelSecondsPlural);
                break;
            case "minutes":
                this.days = new FloorDays(labelDaySingular, labelDayPlural);
                this.hours = new FloorHours(labelHoursSingular, labelHoursPlural);
                this.minutes = new CeilMinutes(labelMinutesSingular, labelMinutesPlural);
                this.seconds = new NoValue(labelSecondsSingular, labelSecondsPlural);
                break;
            case "seconds":
                this.days = new FloorDays(labelDaySingular, labelDayPlural);
                this.hours = new FloorHours(labelHoursSingular, labelHoursPlural);
                this.minutes = new FloorMinutes(labelMinutesSingular, labelMinutesPlural);
                this.seconds = new Seconds(labelSecondsSingular, labelSecondsPlural);
                break;
            default:
                break;
        }
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.interval.cancel(this.refreshCountdown);
    }

    public render(): TemplateResult<1> {
        const [days, hours, minutes, seconds] = this.countdownParts;
        return html`
            <span class="countdown-part days">
                <span class="number">${days.value}</span>
                <span class="label">${days.label}</span>
            </span>
            <span class="countdown-part hours">
                <span class="number">${hours.value}</span>
                <span class="label">${hours.label}</span>
            </span>
            <span class="countdown-part minutes">
                <span class="number">${minutes.value}</span>
                <span class="label">${minutes.label}</span>
            </span>
            <span class="countdown-part seconds">
                <span class="number">${seconds.value}</span>
                <span class="label">${seconds.label}</span>
            </span>
        `;
    }

    private updateCountdownParts(): void {
        const now = Date.now();
        const millisLeft = Math.max(0, this.targetDate - now);
        this.countdownParts = [
            this.days.calculate(millisLeft),
            this.hours.calculate(millisLeft),
            this.minutes.calculate(millisLeft),
            this.seconds.calculate(millisLeft)
        ];
    }
}
